import type { RunbookRunStreamStatus } from '@chatsdet/types';
import type { TagColor } from '@chatsdet/ui';

/**
 * A runbook as returned by `GET /org/properties/:id/runbooks`.
 *
 * @remarks `inputSchema` is the shape `ActionParamsEditor` already knows how to
 * render — the runbook file format normalizes into it deliberately (see
 * `packages/canary-spec/src/runbook-file.ts`'s `ParsedRunbook.inputSchema`), so
 * the trigger dialog reuses that editor rather than growing its own.
 *
 * Null when the runbook declares no inputs, which is most of them.
 */
export type Runbook = {
  id: string;
  slug: string;
  filePath: string;
  name: string;
  description: string | null;
  destructive: boolean;
  parseError: string | null;
  propertyGithubRepoId: string;
  /** The stage a `promote-<stage>` runbook ships to; null for every other runbook. */
  stage: string | null;
  inputSchema: {
    type: 'object';
    properties: Record<
      string,
      {
        type: 'string' | 'boolean';
        description?: string;
        enum?: string[];
        default?: string | boolean;
      }
    >;
    required?: string[];
  } | null;
};

/** A `promote-<stage>` runbook that can actually run — `stage` is narrowed non-null. */
export type PromotionRunbook = Runbook & { stage: string };

/** Promotions are runbooks with a stage that also parsed cleanly (a `parseError` runbook can't run). */
export function isPromotionRunbook(runbook: Runbook): runbook is PromotionRunbook {
  return runbook.stage !== null && runbook.parseError === null;
}

/** `status` reuses the SSE stream's vocabulary — the run row and the stream agree on it by construction. */
export type RunbookRunStatus = RunbookRunStreamStatus;

export type RunbookRun = {
  id: string;
  orgId: string;
  runbookId: string;
  propertyId: string;
  slug: string;
  status: RunbookRunStatus;
  inputs: Record<string, string | boolean>;
  resolvedSha: string;
  triggeredBy: string;
  triggerSource: string;
  microvmRunId: string | null;
  exitCode: number | null;
  logStorageKey: string | null;
  error: string | null;
  startedAt: string | null;
  finishedAt: string | null;
  createdAt: string;
  updatedAt: string;
};

export const runbookRunStatusColors: Record<RunbookRunStatus, TagColor> = {
  queued: 'gray',
  running: 'blue',
  succeeded: 'green',
  failed: 'red',
  cancelled: 'gray',
  timed_out: 'orange',
};

export const runbookRunStatusLabels: Record<RunbookRunStatus, string> = {
  queued: 'Queued',
  running: 'Running',
  succeeded: 'Succeeded',
  failed: 'Failed',
  cancelled: 'Cancelled',
  timed_out: 'Timed out',
};

export function formatDateTime(value: string | null): string {
  if (!value) return '—';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '—';
  return `${date.toLocaleDateString()} ${date.toLocaleTimeString()}`;
}

export function formatDuration(startedAt: string | null, finishedAt: string | null): string {
  if (!startedAt || !finishedAt) return '—';
  const ms = new Date(finishedAt).getTime() - new Date(startedAt).getTime();
  if (!Number.isFinite(ms) || ms < 0) return '—';
  if (ms < 1000) return '<1s';
  const totalSeconds = Math.floor(ms / 1000);
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;
  return minutes === 0 ? `${seconds}s` : `${minutes}m ${seconds}s`;
}

export function shortSha(sha: string): string {
  return sha.slice(0, 7);
}
